import React,{useEffect,useRef,useState} from 'react';
import {View} from 'react-native';
import {LocalApiError} from '../../local-api';
import {gpsCommandError} from '../../../shared/gps-parameters';
import {gt06CommandStatus} from '../../../shared/gt06-command-result';
import {Text} from '../copy';
import {useLive} from '../live';
import {useTokens} from '../theme/tokens';
import {Button} from './primitives';
import {confirmedGpsControl} from '../../../shared/gps-feature-availability.js';
import {newGpsObservation} from '../../position-status';

// The overview and location menus use the same request and feedback flow.
export function GpsPositionActions(){
 const {state}=useLive();
 return <ScopedGpsPositionActions key={JSON.stringify([state.accountEmail,state.signedIn,state.snapshot?.device?.id])}/>;
}
function ScopedGpsPositionActions(){
 const {state,session,locale,tracker}=useLive(),{colors,type}=useTokens();
 const d=state.snapshot?.device,da=locale==='da-DK',t=(a:string,b:string)=>da?a:b;
 const [sending,setSending]=useState(false),[message,setMessage]=useState(''),[cooldown,setCooldown]=useState(0);
 const revision=useRef(0),inFlight=useRef(false);
 const [observation,setObservation]=useState<{id:number;requestedAt:number}|null>(null);
 const control=confirmedGpsControl(d),supported=!!control&&control.position!==false,canRequest=d?.access?.canConfigure!==false;
 const connected=tracker.connection==='online'&&!state.networkUnavailable;
 const disabled=!d||!supported||!canRequest||!connected||(state.busy&&!state.autoRefreshing)||sending||cooldown>0;
 useEffect(()=>{
  revision.current++;inFlight.current=false;setSending(false);setMessage('');setCooldown(0);setObservation(null);
  return()=>{revision.current++;};
 },[d?.id,state.accountEmail]);
 useEffect(()=>{
  if(cooldown<=0)return;
  const timer=globalThis.setTimeout(()=>setCooldown(n=>Math.max(0,n-1)),1000);
  return()=>globalThis.clearTimeout(timer);
 },[cooldown]);
 useEffect(()=>{
  if(!observation)return;
  if(d?.profile?.timeZoneConfirmed&&newGpsObservation(state.snapshot?.position??null,observation.id,observation.requestedAt,Date.now(),true)){
   setMessage(t('En ny GPS-måling er modtaget efter anmodningen.','A new GPS measurement arrived after the request.'));setObservation(null);return;
  }
  const timer=globalThis.setTimeout(()=>{setObservation(null);setMessage(t("Vi modtog ingen ny GPS-position inden for ét minut. Du kan prøve igen.","No new GPS position was received within one minute. You can try again."));},Math.max(1,observation.requestedAt+60000-Date.now()));
  return()=>globalThis.clearTimeout(timer);
 },[observation,state.snapshot?.position,d?.profile?.timeZoneConfirmed,locale]);
 const request=async()=>{
  if(disabled||inFlight.current||!d)return;
  inFlight.current=true;setSending(true);
  const run=revision.current,current=()=>run===revision.current;
  const requestedAt=Date.now();setObservation(null);
  setMessage(t('Anmodningen sendes til trackeren. Afventer svar…','Sending the request to the tracker. Waiting for a reply…'));
  try{
   const result=await session.requestGpsPosition(d.id);
   if(!current())return;
   const status=gt06CommandStatus(result.text,result.status);
   if(status!=='rejected')setObservation({id:state.snapshot?.position?.id??0,requestedAt});
   setMessage(status==='rejected'
    ?t("Trackeren kunne ikke sende en position. Prøv igen senere.","The tracker could not send a position. Try again later.")
    :status==='command_accepted'
    ?t("Trackeren er kontaktet. Kortet opdateres, når en ny GPS-position er modtaget.","The tracker has been contacted. The map updates when a new GPS position is received.")
    :status==='pending'
    ?t('Anmodningen er sendt. Afventer svar og en ny GPS-position fra trackeren.','Request sent. Waiting for a reply and a new GPS position from the tracker.')
    :t('En ny GPS-position er ikke bekræftet endnu. Se tidspunktet for seneste position på kortet.','A new GPS position has not been confirmed yet. Check the latest position time on the map.'));
  }catch(error){
   if(!current())return;
   const code=error instanceof LocalApiError?error.code:'unknown';
   const seconds=error instanceof LocalApiError?error.retryAfterSeconds??60:60;
   if(code==='rateLimited')setCooldown(seconds);
   setMessage(gpsCommandError(code,da,seconds));
  }finally{
   // Read server data after a request; never automatically repeat the command.
   if(current()){
    try{await session.refresh();}catch{}
    if(current()){inFlight.current=false;setSending(false);}
   }
  }
 };
 const explanation=!canRequest?t('Du har læseadgang til trackeren.','You have read-only access to this tracker.')
  :!supported?t('Positionsanmodning er endnu ikke aktiveret i Qtracker for denne model.','Location requests are not yet enabled in Qtracker for this model.')
  :!connected?t('Trackeren er offline, eller forbindelsen er ukendt.','The tracker is offline or its connection is unknown.'):null;

 const textStyle={color:colors.textMuted,fontSize:type.body,lineHeight:type.line};
 return <View style={{gap:10}}>
  <Button compact minHeight={48} fontSize={16} label={sending?t('Afventer trackerens svar…','Waiting for the tracker…'):t('Anmod trackeren om position','Request a position from the tracker')} icon="position" disabled={disabled} onPress={()=>void request()}/>
  {explanation&&<Text style={textStyle}>{explanation}</Text>}
  {cooldown>0&&<Text style={textStyle}>{t(`Vent ${cooldown} sekunder før en ny anmodning.`,`Wait ${cooldown} seconds before making another request.`)}</Text>}
  {!!message&&<Text accessibilityLiveRegion="polite" style={{...textStyle,color:colors.text}}>{message}</Text>}

 </View>;
}
